import React, { Ref, useState } from "react";
import {
  MailIcon,
  LockOpenIcon,
  EyeIcon,
  EyeOffIcon,
  ArrowLeftIcon,
} from "@heroicons/react/solid";
import { useRouter } from "next/router";
import Link from "next/link";
import { useMutation } from "@tanstack/react-query";
import { translate } from "utils";
import { api } from "utils/api";
import { navigateAfterAuth } from "utils/publicOfferNavigation";

interface FormData {
  email: string;
  password: string;
}
const initialFormData: FormData = {
  email: "",
  password: "",
};

export const OfferSignIn = ({
  handleAuthForm,
  type = "appointment",
  emailRef = null,
  publicId,
  offerId,
  code,
}: {
  handleAuthForm(a): void;
  type?: "appointment" | "product" | "tip" | "community" | "event";
  emailRef?: Ref<HTMLInputElement>;
  publicId: string;
  offerId: string;
  code?: string;
}) => {
  const router = useRouter();
  const signIn = useMutation((payload: { email: string; password: string }) =>
    api.post("/users/v1/auth/signin", payload)
  );
  const [formData, setFormData] = useState(initialFormData);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSignInLoading, setIsSignInLoading] = useState<boolean>(false);

  const resolvedPath = `/${publicId}/${type}/${offerId}`;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      // Trimming any whitespace
      [e.target.name]: e.target.value.trim().replace(/\s/g, ""),
    });
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSignInLoading(true);
    try {
      await signIn.mutateAsync(formData);
      await navigateAfterAuth(
        router,
        resolvedPath,
        code ? { promo: code } : {}
      );
    } catch (e) {
      return;
    } finally {
      setIsSignInLoading(false);
    }
  };

  const handleToggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const isDisabled = isSignInLoading || !formData.email || !formData.password;

  const handleBack = () => {
    handleAuthForm(null);
  };

  return (
    <>
      <div className="text-center">
        <div
          onClick={handleBack}
          className="inline-flex items-center justify-center border border-slate-500 p-3 rounded-full cursor-pointer mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 text-slate-500" />
        </div>
        <h2 className="text-xl font-medium text-slate-500">
          {translate("signInToContinue")}
        </h2>
      </div>
      <form
        className="flex flex-col w-full mt-6 font-inter"
        onSubmit={handleSubmitForm}
      >
        <div className="relative rounded-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MailIcon className="h-5 w-5 text-slate-500" aria-hidden="true" />
          </div>
          <input
            type="email"
            name="email"
            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 py-3 sm:text-sm rounded-md text-slate-700 border-black/10 font-medium"
            placeholder={translate("enterEmail")}
            onChange={handleChange}
            ref={emailRef}
          />
        </div>
        <div className="relative rounded-md mt-6">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <LockOpenIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 py-3 sm:text-sm rounded-md text-slate-700 border-black/10 font-medium"
            placeholder={translate("enterPassword")}
            onChange={handleChange}
          />
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer">
            {showPassword ? (
              <EyeOffIcon
                className="h-5 w-5 text-gray-400 hover:text-gray-500 ease-in-out duration-300"
                aria-hidden="true"
                onClick={handleToggleShowPassword}
              />
            ) : (
              <EyeIcon
                className="h-5 w-5 text-gray-400 hover:text-gray-500 ease-in-out duration-300"
                aria-hidden="true"
                onClick={handleToggleShowPassword}
              />
            )}
          </div>
        </div>
        {signIn.error ? (
          <div className="mt-2 text-xs font-medium text-ruslan-red ease-in-out duration-300">
            {translate("emailOrPassword")}
          </div>
        ) : null}
        <div className="mt-2 text-right">
          <Link href="/password-forgot">
            <a className="text-xs text-slate-500 hover:text-slate-900 font-medium ease-in-out duration-300">
              {translate("forgotPassword")}
            </a>
          </Link>
        </div>
        <div className="mt-6">
          <button
            type="submit"
            disabled={isDisabled}
            className="w-full flex justify-center items-center p-3 rounded-md text-sm font-semibold text-white bg-ruslan-red hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black focus:ring-red-400 disabled:opacity-75 disabled:pointer-events-none"
          >
            {isSignInLoading ? (
              <span className="flex animate-spin mr-3">
                <span className="rounded-full border-2 border-white border-b-black h-4 w-4" />
              </span>
            ) : null}
            {translate("logIn")}
          </button>
        </div>
        <div className="flex justify-center mt-6 text-custom-light-slate text-center text-sm font-medium font-poppins">
          {translate("dontHaveAccount")}
          <p
            onClick={() => handleAuthForm("sign-up")}
            className="ml-1 text-custom-dark-blue cursor-pointer"
          >
            {translate("createAccount")}
          </p>
        </div>
      </form>
    </>
  );
};
